import React, { useEffect, useReducer, useRef, useState } from 'react';
import { HashRouter as Router, Route, Redirect } from 'react-router-dom';
import { FormattedMessage, injectIntl } from 'react-intl';
import { colorToArray, encodeBase64Fake, getLayout, getRemotedbUrl, i18nIntl, isNotEmpty, isColorBright, RGB_2_HEX, cleanGetParamUrl } from '../lib/ep';
import { useCSS } from '../lib/hooks.js';

import ActionButton, { MainButton, SecondaryButton, SquaredButton } from '../components/builder/ActionButton';
import ActionLink from '../components/builder/ActionLink';
import ButtonCollection from '../components/builder/ButtonCollection.js';
import { DisplayDynamicForm } from '../components/builder/DynamicForm.js';
import Input from '../components/builder/Input';
import Loader from '../components/builder/Loader';
import { SuccessMessage } from '../components/builder/Message.js';
import Model from '../components/builder/Model';
import { NavigationTabsBuilder } from '../components/builder/NavigationTabs.js';
import { parsePhone } from '../components/builder/PhoneField.js';
import SaveButton from '../components/builder/SaveButton.js';
import Spinner from '../components/builder/Spinner.js';
import TextArea from '../components/builder/TextArea.js';
import TimeCounterSimple from '../components/builder/TimeCounterSimple.js';
import TimerBar from '../components/builder/TimerBar.js';

import { QrScanInput } from '../components/ui_items/QRScan.js';
import QRViewer from '../components/ui_items/QRViewer.js';

import FloatingLayerPublic from '../components/front/FloatingLayerPublic.js';

import LeveledHTMLText from '../components/front/helpers/LeveledHTMLText.js';

import { RedeemedStatus } from '../components/prizes/RedeemPrize.js';
import WinnerContent from '../components/prizes/WinnerContent.js';

import ExportModal from '../components/backend/ExportModal.js';

const total_refresh_cycle = 10 * 60; // in sec

const QrListTimer = ({state, ...props}) => {
	useEffect(() => {
		return () => {
			if(props.onUnmount) props.onUnmount();
		}
	}, []);

	if(props.seconds == undefined)return null;

	const pending_time = parseInt(props.seconds);	// in sec

	return (
		<div class="qr_timer">
			<TimeCounterSimple
				seconds={pending_time}
				hide_days={true}
				hide_hours={true}
			>Time remaining until refresh</TimeCounterSimple>

			<TimerBar
				size="small"
				initial={pending_time / total_refresh_cycle}
				animation_duration={pending_time}
			/>
		</div>
	)
};

const QRListWrapper = (props) => {
	const type = props?.type ?? 'qr';

	const changeType = props?.changeType ?? false;
	let display_tabs = props?.display_tabs ?? false;
	if(changeType === false) display_tabs = false;

	let sections = [];
	if(display_tabs) {
		sections = [
			{ label:"QR", icon:'knk-qr', onClick:() => changeType('qr'), selected:(type == 'qr') ? true : false },
			{ label:"List", icon:'knk-list', onClick:() => changeType('list'), selected:(type == 'list') ? true : false },
		];
	}

	return (
		<>
			{(display_tabs) && (
				<NavigationTabsBuilder
					sections={sections}
					//in_frontend={true}
					_class={{ other:'gui_not_in_top_big' }}
				/>
			)}

			{props.children}
		</>
	)
};

const ItemsList = ({ items, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);
	const loaded = props.state?.loaded ?? false;
	if(items.length == 0) {
		if(loaded){
			return (
				<div class="content gui_text_align_center" style={{height:'35px'}}>{i18n("Currently there are no active promotions.")}</div>
			)
		}
		if(!loaded){
			return (
				<QRListWrapper type='list' changeType={props.setType} display_tabs={props.display_tabs}>
					<ul class="qr_list gui_pos_rel">
						<li class="gui_not_in_top_huge">
							<Spinner>{i18n("Loading")}...</Spinner>
						</li>
					</ul>
				</QRListWrapper>
			)
		}
	}

	const basename = props?.basename ?? 'list';
	const selected = props?.selected ?? false;

	const displaySingleItem = (id) => {
		window.location.hash = (id) ? `/${basename}/${id}`: `/${basename}/`;
	};

	return (
		<QRListWrapper type='list' changeType={props.setType} display_tabs={props.display_tabs}>
			<ul class="qr_list">
				{items.map((item, n) => {
					if(selected !== false && item.id != selected) return null;

					return (
						<li key={`qr_${n}`}>
							<div class="qr_contents" onClick={() => displaySingleItem(item.id)}>
								<div class="no_qr">
									<div class="textual_contents">
										<strong class="stage_title">{item.title}</strong>

										<em class="until_date">{props.intl.formatMessage({ id:"Promotion valid until {date}." }, { date:EPDateFormat(item.end_date, 'L') })}</em>
									</div>

									<span class='icon ep-next' />
								</div>
							</div>
						</li>
					)
				})}
			</ul>
		</QRListWrapper>
	)
};

const ChangeParticipationNumber = ({ qr, state, dispatch, ...props }) => {
	const quantity = qr?.qty ?? 1;

	return (
		<div class="qr_contents">
			<ButtonCollection
				_class={{ other:'footer qr' }}
			>
				<SquaredButton
					icon="knk-minus mainBgIcon mainTextButton"
					onClick={() => dispatch({ type:'CHANGE_QTY', id:qr.id, k:'qty', v:Math.max(1, quantity - 1) })}
					disabled={(quantity == 1) ? true : false}
				/>

				<span class="resumee">{quantity}</span>

				<SquaredButton
					icon="knk-plus mainBgIcon mainTextButton"
					onClick={() => dispatch({ type:'CHANGE_QTY', id:qr.id, k:'qty', v:(quantity + 1) })}
				/>
			</ButtonCollection>
		</div>
	)
};

const SingleQR = ({ qr, state, dispatch, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);
	const basename = props?.basename ?? 'list';
	const selected = props?.selected ?? false;
	const is_multiple = qr?.is_multiple ?? false;

	const displaySingleQR = (id) => {
		window.location.hash = (id) ? `/${basename}/${id}`: `/${basename}/`;
	};

	return (
		<>
			<h2 class="section_title">{qr?.title ?? i18n("Show this QR")}</h2>

			<div class={`qr_contents${(selected !== false) ? ' selected' : ''}`}>
				<div class="qr" onClick={(selected === false) ? () => displaySingleQR(qr.id) : null}>
					{(props?.loading_qr ?? false) ? (
						<div class="gui_qr_viewer">
							<div class="gui_pos_rel width_100">
								<Loader />
							</div>
						</div>
					) : (
						<QRViewer
							url={qr.url}
							size={220}
							level='H'
							id='content_qr_link'
							filename={`qr_${qr.code}`}
							onFinish={() => displaySingleQR(false)}
							hide_actions={true}
						/>
					)}
				</div>

				{(is_multiple) && (
					<ChangeParticipationNumber
						qr={qr}
						state={state}
						dispatch={dispatch}
						intl={props.intl}
					/>
				)}
			</div>
		</>
	)
};

const QRList = ({ state, dispatch, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ type, setType ] = useState(props?.type ?? 'qr');	// 'qr' | 'list'

	const selected = props?.selected ?? false;
	const is_builder = props?.is_builder ?? false;

	const basename = props?.basename ?? 'list';

	const getQR = (cb) => {
		if(state.changing == null) {
			dispatch({ type:'CHANGE', k:'loaded', v:false });
		}

		$.post({
			url:getRemotedbUrl(`/sales/${props.promotion.id}/${props.hash}/stages`),
			data:{
				//action:'stages',
				_data:encodeBase64Fake({ action:'stages', point:state.point.id, totals:state.totals}),
			},
			dataType:'JSON',
			success:(response) => {
				if(response.status) {
					const new_code_stages = [];
					for(var i in response.stages) {
						const _qr = { ...response.stages[i] };
						if(state.totals[_qr['id']] != undefined) _qr['qty'] = state.totals[_qr['id']];
						if(_qr['qty']== undefined) _qr['qty'] = 1; //Initial quantity if not defined
						_qr['url'] = props.generateURL(_qr, state.point);
						new_code_stages.push(_qr);
					}

					if(props.onGetData != undefined){
						const remaining_time = parseInt(response.expire_at) - parseInt(response.now);
						props.onGetData(remaining_time);
					}

					dispatch({ type:'CHANGE', k:'stages', v:new_code_stages });

					setTimeout(() => {
						dispatch({ type:'CHANGE', k:'loaded', v:true });
						if(cb != undefined) cb();
					}, 300);
				}
			}
		});
	};

	useEffect(() => {
		getQR();
	}, []);

	useEffect(() => {
		if(state.changing == null) return;

		if(props.sendTotals != undefined) props.sendTotals(state.totals);
	}, [ state.changing ]);

	if(!state.loaded && !is_builder) {
		return (
			<div class="gui_not_in_top_big gui_not_in_bottom_small gui_text_align_center">
				{i18n("Generating a new QrCodes to scan.")}
				<Loader />
			</div>
		)
	}

	if(state.stages.length == 0 && !is_builder) {
		return (
			<div class="gui_not_in_top_big gui_not_in_bottom_small">
				{i18n("There is nothing to display")}
			</div>
		)
	}
	if(type == 'list' && selected === false) {
		return (
			<ItemsList
				items={state.stages}
				selected={selected}
				basename={basename}
				display_tabs={props.display_tabs}
				setType={setType}
				state={state}
				intl={props.intl}
			/>
		)
	}

	return (
		<QRListWrapper changeType={setType} display_tabs={props.display_tabs}>
			<ul class="qr_list">
				{state.stages.map((qr, n) => {
					if(selected !== false && qr.id != selected) return null;

					return (
						<li key={`qr_${n}`}>
							<SingleQR
								qr={qr}
								selected={selected}
								loading_qr={(n == state.changing) ? true : false}
								state={state}
								dispatch={dispatch}
								basename={basename}
								intl={props.intl}
							/>
						</li>
					)
				})}
			</ul>
		</QRListWrapper>
	)
};

const Wrapper = ({ children, state, dispatch, header_props = {}, ...props }) => {
	const is_login = props?.is_login ?? false;
	const is_builder = props?.is_builder ?? false;
	const enable_logout = props?.enable_logout ?? false;

	if(!(state?.point?.id ?? null) && !is_builder && !enable_logout) {
		return (
			<Redirect to='/' />
		)
	}

	const classes = ['pos_contents'];
	if(is_login) classes.push('is_login');

	return (
		<section class="points_of_sale_portal">
			<Header
				state={state}
				dispatch={dispatch}
				{...props}
				{...header_props}
			/>

			<article class={classes.join(" ")}>
				{children}

				<Footer
					{...props}
				/>
			</article>
		</section>
	)
};

const Footer = (props) => {
	let bottom_image = false;
	if(isNotEmpty(props.promotion, 'bottom_image')) bottom_image = props.promotion.bottom_image;

	if(bottom_image === false) return null;

	return (
		<div class="bottom_image">
			<img src={bottom_image} width="100%" alt="Bottom image" />
		</div>
	)
};

const Header = ({ state, dispatch, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ menu_open, setMenuOpen ] = useState(false);
	
	const is_builder = props?.is_builder ?? false;
	const display = props?.display ?? true;
	const pos_name = (isNotEmpty((state?.point ?? {}), 'name')) ? state.point.name : false;

	//let logo = props?.promotion?.logo ?? false;
	let logo = false;
	if(isNotEmpty(state.point, 'full_image')) logo = state.point.full_image;

	const logout = () => {
		dispatch({ type:'CHANGE', k:'point',v:false });
		setTimeout(() => {
			$.get(getRemotedbUrl(`/sales/${props.promotion.id}/${props.hash}/logout`), () => {
				window.location.hash = '/';
			});
		}, 500);
	};

	const back_url = props?.back_url ?? false;

	return (
		<>
			<header class={`header${(menu_open) ? ' open' : ''}`}>
				<div class="header_info">
					{(logo) && (
						<div class="logo">
							<img src={logo} height="100%" alt="Logo" />
						</div>
					)}

					<div class="header_title">
						<h1>{(pos_name) ? pos_name : i18n("Sign in")}</h1>
					</div>
				</div>

				{(display) && (
					<div class="actions">
						{((props?.enable_back ?? true) && !menu_open) && (
							<SquaredButton
								icon='ep-prev'
								_href={(back_url !== false) ? back_url :null}
								onClick={(back_url === false) ? () => history.back() : null}
								_class={{ other:`menu_btn` }}
							/>
						)}

						<SquaredButton
							icon={(!menu_open) ? 'ep-burguer-menu' : 'ep-delete-circle'}
							_class={{ other:`menu_btn${(menu_open) ? ' opened' : ''}` }}
							onClick={() => setMenuOpen(!menu_open)}
						/>
					</div>
				)}
			</header>

			{(!is_builder) && (
				<nav class={`menu${(menu_open) ? ' open' : ''}`}>
					<span class='tapper' onClick={() => setMenuOpen(false)} />

					<ul>
						{(props?.has_help ?? false) && (
							<li>
								<ActionLink
									_href='#help'
								>Help</ActionLink>
							</li>
						)}

						<li>
							<ActionLink
								onClick={logout}
							>Logout</ActionLink>
						</li>
					</ul>
				</nav>
			)}
		</>
	)
};

const ActionSelectorPage = ({ state, dispatch, ...props }) => {
	return (
		<>	
			<Wrapper
				state={state}
				dispatch={dispatch}
				is_builder={props.is_builder}
				has_help={props.has_help}
				promotion={props.promotion}
				hash={props.hash}
				header_props={{
					enable_back:false,
				}}
				intl={props.intl}
			>
				<ul class='main_action ctas big_buttons'>
					<li>
						<ActionButton
							_fullclass=""
							_href='#qr_list'
						>Promotions with QR</ActionButton>
					</li>

					{(props.promotion?.code_validator ?? false) && (
						<li>
							<ActionButton
								_fullclass=""
								_href='#validation_portal'
							>Redeem prizes</ActionButton>
						</li>
					)}

					{(props?.has_help ?? false) && (
						<li>
							<ActionButton
								_fullclass="secondary"
								_href='#help'
							>Help</ActionButton>
						</li>
					)}
				</ul>
			</Wrapper>
		</>
	)
};

const QRListPage = ({ state, dispatch, ...props }) => {
	return null;

	const single_id = props?.single_id ?? false;

	const [ tab_visible, setTabVisible ] = useState(true);
	const [ pending_time, setPendingTime ] = useState(null);
	const [ loading_qrs, setLoadingQrs ] = useState(true);
	const [ loop, setLoop ] = useState(0);
 	
	const mount_time = useRef(null);
	const unmount_time = useRef(null);
	const load_time = useRef(null);
	const expiring_time = useRef(null);
	const total_time = useRef(null);
	const timeout = useRef(null);
	const first_load = useRef(true);

	const prepareNextRevalidation = (sec) => {
		timeout.current = setTimeout(() => {
			revalidate();
		}, [ sec * 1000 ]);
	};

	const cancelNextRevalidation = () => {
		window.clearTimeout(timeout.current);
		timeout.current = null;
	};

	const onTabVisibilityChange = () => {
		const is_visible = (document.visibilityState != 'hidden') ? true : false;

		const now = new Date();
		if(is_visible) {
			mount_time.current = now.getTime();
			const needs_revalidate = checkIfRevalidate();
			if(!needs_revalidate) {
				const active_time = (mount_time.current - load_time.current) / 1000;
				const inactive_time = (mount_time.current - unmount_time.current) / 1000;
				const remaining_time = total_time.current - active_time - inactive_time;

				total_time.current = remaining_time;

				setPendingTime(remaining_time);

				prepareNextRevalidation(remaining_time);
			} else {
				revalidate();
			}
		} else {
			unmount_time.current = now.getTime();
			cancelNextRevalidation();
		}
		setTabVisible(is_visible);
	}

	useEffect(() => {
		window.addEventListener('visibilitychange', onTabVisibilityChange);
		setLoadingQrs(false);

		return () => {
			window.removeEventListener('visibilitychange', onTabVisibilityChange);
			cancelNextRevalidation();
		}
	}, []);

	const revalidate = () => {
		setLoadingQrs(true);
		setTimeout(()=>{
			setLoadingQrs(false);
			total_time.current = total_refresh_cycle;
			setPendingTime(total_refresh_cycle);
			setLoop(loop + 1);

			prepareNextRevalidation(total_refresh_cycle);
		},0);
	};

	const keepUnmountTime = () => {
		const date = new Date();
		unmount_time.current = date.getTime();
	}

	const onQRLoad = (remaining_time) => {
		if(first_load.current) {
			total_time.current = remaining_time;
			prepareNextRevalidation(remaining_time);
			setPendingTime(remaining_time);
			first_load.current = false;
		}

		const now = new Date();
		load_time.current = now.getTime();
		expiring_time.current = remaining_time * 1000 + now.getTime();
	}

	const checkIfRevalidate = () => {
		return (mount_time.current > expiring_time.current) ? true : false;
	}

	const updateTotals = (totals) => {
		dispatch({ type:'CHANGE', k:'totals',v:totals})
	};

	return (
		<Wrapper
			state={state}
			dispatch={dispatch}
			is_builder={props.is_builder}
			has_help={props.has_help}
			promotion={props.promotion}
			hash={props.hash}
			intl={props.intl}
		>
			{(tab_visible && !loading_qrs) && (
				<QrListTimer
					key={loop}
					state={state}
					dispatch={dispatch}
					promotion_id={props.promotion.id}
					hash={props.hash}
					onUnmount={keepUnmountTime}
					seconds={pending_time}
				/>
			)}

			{(!loading_qrs) && (
				<>
					<div id="nav_tabs" />

					<QRList
						{...props}
						display_tabs={true}
						state={state}
						dispatch={dispatch}
						selected={single_id}
						totals={state.totals}
						sendTotals={updateTotals}
						onGetData={onQRLoad}
						basename='qr_list'
					/>
				</>
			)}
		</Wrapper>
	)
};

const SimpleListPage = ({ state, dispatch, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const getStages = () => {
		dispatch({ type:'CHANGE', k:'loaded', v:false });
		$.post({
			url:getRemotedbUrl(`/sales/${props.promotion.id}/${props.hash}/stages`),
			data:{
				//action:'stages',
				_data:encodeBase64Fake({ action:'stages', point:state.point.id }),
			},
			dataType:'JSON',
			success:(response) => {
				dispatch({ type:'CHANGE', k:'stages', v:response });
				dispatch({ type:'CHANGE', k:'loaded', v:true });
			}
		});
	};

	useEffect(() => {
		if(state.stages.length == 0) getStages();
	}, []);
	
	return (
		<Wrapper
			state={state}
			dispatch={dispatch}
			is_builder={props.is_builder}
			has_help={props.has_help}
			promotion={props.promotion}
			back_url='#selector'
			hash={props.hash}
			intl={props.intl}
		>
			<h2 class="section_title">{i18n("Active promotions")}</h2>

			<ItemsList
				items={state.stages}
				selected={props.selected}
				basename={props.basename}
				display_tabs={false}
				intl={props.intl}
				state={state}
			/>
		</Wrapper>
	)
};

const SimpleSingleItemPage = ({ id, state, dispatch, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const qr = state.stages.find((s) => (s.id == id));

	const timeout = useRef(null);
	const done = useRef(null);

	const is_multiple = qr?.is_multiple ?? false;
	const basename = props?.basename ?? 'list';

	const [ display_qr, setDisplayQR ] = useState(false);
	const [ done_button, setDoneButton ] = useState(false);
	const [ qr_url, setQrURL ] = useState(false);
	const [ loading, setLoading ] = useState(false);

	const goBack = () => {
		dispatch({ type:'CHANGE_QTY', id:qr.id, k:'qty', v:1 });	//reset a 1
		window.location.hash = `/${basename}`;
	}
	
	const skeduleGoingBack = () => {
		timeout.current = setTimeout(() => {
			goBack();
		}, (60 * 1000));
	};

	const skeduleDoneButton = () => {
		done.current = setTimeout(() => {
			setDoneButton(true);
		}, (5 * 1000));
	};

	const fetchQRCode = (cb) => {
		setLoading(true);
		setDoneButton(false)
		$.post({
			url:getRemotedbUrl(`/sales/${props.promotion.id}/${props.hash}/new`),
			data:{
				//action:'new',
				_data:encodeBase64Fake({ action:'new', point:state.point.id, stage:qr }),
			},
			dataType:'JSON',
			success:(response) => {
				if(response.status) {
					setQrURL(props.generateURL(response.stage_qr, state.point));
				}
				setLoading(false);
				skeduleDoneButton();
				if(cb != undefined) cb();
			}
		});
	};

	const getQRWithQty = (cb) => {
		setDisplayQR(true);
		fetchQRCode(() => {
			skeduleGoingBack();
			if(cb != undefined) cb();
		});
	};

	useEffect(() => {
		if(!is_multiple) getQRWithQty();

		return () => {
			window.clearTimeout(timeout.current);
			window.clearTimeout(done.current);
		}
	}, []);

	if(!qr) window.location.hash = 'list';

	const display_multipicker = (!display_qr && is_multiple) ? true : false;

	return (
		<Wrapper
			state={state}
			dispatch={dispatch}
			is_builder={props.is_builder}
			has_help={props.has_help}
			promotion={props.promotion}
			hash={props.hash}
			intl={props.intl}
		>
			{(display_multipicker) && (
				<h2 class="section_title">{i18n("How many stamps do you want to give?")}</h2>
			)}

			<ul class="qr_list">
				<li>
					{(display_multipicker) && (
						<div>
							<ChangeParticipationNumber
								qr={qr}
								state={state}
								dispatch={dispatch}
								intl={props.intl}
							/>

							<ButtonCollection
								_class={{ other:'gui_not_in_top_big' }}
							>
								<SaveButton
									onClick={(e, cb) => getQRWithQty(cb)}
								>Continue</SaveButton>
							</ButtonCollection>
						</div>
					)}

					{(!display_multipicker && display_qr) && (
						<>
							<SingleQR
								qr={{ ...qr, url:qr_url, is_multiple:false }}
								loading_qr={loading}
								selected={true}
								state={state}
								dispatch={dispatch}
								basename={props.basename}
								intl={props.intl}
							/>

							<TimerBar
								_class={{other:'not_in_top'}}
								size="small"
								initial={1}
								color_highlight={false}
								animation_duration={60}
							>{i18n("You have 1 minute to scan the QR code")}</TimerBar>

							{(done_button) && (
								<ButtonCollection
									_class={{ other:'gui_not_in_top_big' }}
								>
									<MainButton
										onClick={goBack}
									>Done</MainButton>
								</ButtonCollection>
							)}
						</>
					)}
				</li>
			</ul>
		</Wrapper>
	)
};

const ValidationsPage = ({ state, dispatch, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ loaded, setLoaded ] = useState(false);
	const [ my_validations, setMyValidations ] = useState([]);
	const [ export_validations, setExportValidations ] = useState(false);

	const format_date = ['es'].includes(props.language)  ? 'DD-MM-YYYY HH:mm [h]' : 'LLL';

	const getValidations = () => {
		if(state?.point?.id ?? false) {
			$.post(getRemotedbUrl(`/prizes/getValidations/${props.promotion.id}`), { validation_site_id:state.point.id, promotion_id:props.promotion.id }, (response) => {
				if(response.status == 1) setMyValidations(response.validations);
				setLoaded(true);
			}, 'JSON');
		}
	};

	useEffect(() => {
		getValidations();
	}, []);

	if(!loaded) {
		return (
			<Wrapper
				state={state}
				dispatch={dispatch}
				is_builder={props.is_builder}
				has_help={props.has_help}
				promotion={props.promotion}
				hash={props.hash}
				intl={props.intl}
			>
				<h2 class="section_title">{i18n("Last validations")}</h2>
				<div class="gui_pos_rel">
					<Spinner
						_class={{fullscreen:true}}
						//color='rgba(var(--main-bg-color), 1)'
						//force_bg={true}
					>Checking validations</Spinner>
				</div>

				
			</Wrapper>
		)
	}

	return (
		<Wrapper
			state={state}
			dispatch={dispatch}
			is_builder={props.is_builder}
			has_help={props.has_help}
			promotion={props.promotion}
			hash={props.hash}
			intl={props.intl}
		>
			<h2 class="section_title">{i18n("Last validations")}
				{(my_validations.length > 0) && (<span class="legend gui_not_in_start_tiny">[<ActionLink onClick={()=>setExportValidations(true)}>Export</ActionLink>]</span>)}
			</h2>
			{(my_validations.length == 0) && (
				<div class="gui_text_align_center">{i18n("There are no results.")}</div>
			)}
			{(my_validations.length > 0) && (
				<ul class="validations_list">
					{my_validations.slice(0, 40).map((val, i) => {
						const counter = my_validations.length - i;
						const tz = props.promotion.timezone;
						const pos = (my_validations.length > 10 && counter < 10) ? `0${counter}` : counter;
						return (
							<li key={`p${i}`}>
								<dl class="winner_data">
									<div>
										<dt><strong>{i18n("Name")}</strong>:</dt>
										<dd>{val.name}</dd>
									</div>

									<div>
										<dt><strong>{i18n("Prize")}</strong>:</dt>
										<dd>{val.prize}</dd>
									</div>

									<div>
										<dt><strong>{i18n("Code")}</strong>:</dt>
										<dd>{val.code}</dd>
									</div>
								</dl>

								<div class="item_footer">
									<span>#{pos}</span>
									<span>{EPGetTimezone(val.created, tz, format_date)}</span>
								</div>
							</li>
						)
					})}
				</ul>
			)}
			{(export_validations) && (
				<ExportModal
					is_public={true}
					type="validation_sites"
					toggle={()=>setExportValidations(false)}
					validation_site={state.point}
					promotion={props.promotion}
					promotion_id={props.promotion.id}
				/>
			)}
		</Wrapper>
	)
};

const RedeemStatus = ({ type, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const text_color = 'var(--ep-white-color), 1';
	let bg_color = 'var(--ep-red-color)';
	let message;
	let icon = 'ep-delete-circle';
	switch(type) {
		case 'valid':
			icon = 'ep-check-list-item';
			message = `<h1>${i18n("Prize validated")}</h1>`;
			bg_color = 'var(--ep-green-color)';
			break;

		case 'invalid':
		case 'used':
			message = `<h1>${i18n("Not valid")}</h1>`;
			break;

		case 'expired':
			message = `<h1>${i18n("This prize has already expired")}</h1>`;
			break;

		case 'notvalidated':
			message = `<h1>${i18n("This code has not yet been validated")}</h1>`;
			break;
	}

	return (
		<RedeemedStatus
			message={message}
			icon={icon}
			bg_color={bg_color}
			text_color={text_color}
			intl={props.intl}
		/>
	)
};

const ValidationPortal = ({ state, dispatch, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);
	const [ state_cv, dispatch_cv ] = useReducer((state, action) => {
		switch(action.type) {
			case 'SET_ERRORS':
				return {
					...state,
					errors:action.errors,
				};

			case 'SET_INVALID':
				return {
					...state,
					validated:'invalid',
					code:'',
				};

			case 'SET_EXPIRED':
				return {
					...state,
					validated:'expired',
					expired:true,
				};

			case 'SET_VALID':
				return {
					...state,
					validated:'valid',
					expired:false,
					prize:action?.prize ?? null,
					validation_comment:'',
					CodeValidation:{},
				};

			case 'SET_CHECKED':
				return {
					...state,
					validated:'checking',
					expired:false,
					prize:action?.prize ?? null,
					validation_comment:'',
					CodeValidation:{},
				};

			case 'SET_USED':
				return {
					...state,
					validated:'used',
					expired:action?.expired ?? 0,
					prize:action?.prize ?? null,
					CodeValidation:action?.CodeValidation ?? {},
				};

			case 'RESET':
				return {
					...state,
					code:'',
					validated:null,
					prize:null,
				};

			case 'RESET_UNREDEEM':
				return {
					...state,
					unredeeming:true,
					validate:0,
					prize:null,
				};

			case 'SET_UNREDEEM':
				return {
					...state,
					code:'',
					prize:null,
				};

			case 'CHANGE':
				return {
					...state,
					[action.k]:action.v,
				};
	
			default:
				return state;
		}
	}, {
		code:state.param_code,
		validated:null,
		expired:false,
		prize:null,
		display_comment_layer:false,
		display_prize_layer:false,
		display_pin_layer:false,
		validation_comment:'',
		show_ticket:false,
		CodeValidation:{},
		errors:{},
		unredeeming:false,
	});

	const [ scan_error, setScanError ] = useState(false);
	const promotion = props?.promotion ?? {};
	const format_date = ['es'].includes(props.language)  ? 'DD-MM-YYYY HH:mm [h]' : 'LLL';
	
	useEffect(() => {
		if(scan_error) {
			setTimeout(() => {
				setScanError(false);
			}, 3000);
		}
	}, [ scan_error ]);

	const assignCodeFromScan = (content_url) => {
		let _code = null;

		try {
			const url = new URL(content_url);
			const urlParams = new URLSearchParams(url.search);
			_code = urlParams.get('code');
		} catch (error) {
			//NO URL FOUND, All Content IN
			setScanError(false);
			dispatch_cv({ type:'CHANGE', k:'code', v:content_url });
			return;
		}

		if(!_code) {
			setScanError(true);
			return;
		}

		const url_to_check = `sales/${promotion.id}/${promotion.code_validator_hash}?code=${_code}`;
		const old_url_to_check = `cv/${promotion.id}/${promotion.code_validator_hash}?code=${_code}`;
		if(content_url.includes(url_to_check) || content_url.includes(old_url_to_check)) {
			setScanError(false);
			dispatch_cv({ type:'CHANGE', k:'code', v:_code }); //2
		} else {
			setScanError(true);
		}
	};

	const sendCode = (cb, check_code) => {
		if(check_code == undefined) check_code = false;
		const action = check_code ? 'check' : 'redeem';

		$.post({
			url:getRemotedbUrl(`/sales/${promotion.id}/${props.hash}/${action}`),
			data:{
				//action:'login',
				_data:encodeBase64Fake({ action, code:state_cv?.code??'', site:state.point.id }),
			},
			dataType:'JSON',
			success:(response) => {
				setTimeout(() => {
					const result = (response.status == 1) ? 'valid' : (response?.reason ?? 'invalid');
					switch(result) {
						case 'checking':
							//1
							dispatch_cv({ type:'SET_CHECKED', prize:response.prize, code:state_cv?.code??'' });
							break;

						case 'valid':
							//1
							dispatch_cv({ type:'SET_VALID', prize:response.prize });
							break;

						case 'pos':
						case 'invalid':
							//2
							dispatch_cv({ type:'SET_INVALID' });
							break;

						case 'used':
							//3
							dispatch_cv({ type:'SET_USED', ...response });
							break;

						case 'expired':
							//4
							dispatch_cv({ type:'SET_EXPIRED' });
							break;
					}
				},0);

				if(cb != undefined) cb();
			}
		});
	};

	useEffect(() => {
		if(isNotEmpty(state,'param_code',[''])){
			window.location.hash = '#validation_portal';
			setTimeout(() => {
				const check_code = isNotEmpty(promotion,'enable_review_coupon',['']) ? true : false;
				sendCode(()=>{
					dispatch({ type:'CHANGE', k:'param_code', v:'' });
					cleanGetParamUrl('code');
				},check_code);
			}, 300);
		}
	},[])

	const submitComment = (cb) => {
		if(state_cv.validation_comment == '') {
			dispatch_cv({ type:'RESET' });
			return;
		}

		$.post(getRemotedbUrl(`/promotions/prize_validator_comment/${promotion.id}/${promotion.code_validator_hash}`), {
			code_validation_id:state_cv.prize.CodeValidation.id,
			site:state.point.id,
			validation_comment:state_cv.validation_comment,
		}, (response) => {
			setTimeout(() => {
				dispatch_cv({ type:'CHANGE', k:'display_comment_layer', v:false });
				dispatch_cv({ type:'CHANGE', k:'CodeValidation', v:response?.CodeValidation ?? null });
				if(cb != undefined) cb();
			}, 300);
		}, 'JSON');
	};

	const invalidate = () => {
		if(!state_cv.code) {
			dispatch_cv({ type:'RESET' });
			return;
		}

		dispatch_cv({ type:'RESET_UNREDEEM' });
		$.post(getRemotedbUrl(`/promotions/prize_validator_unredeem/${promotion.id}/${promotion.code_validator_hash}`), {
			code:state_cv.code,
			unredeeming:true,
		}, (response) => {
			const status_invalidate = (response.status == 1) ? 'unredeemed' : (response?.result ?? response?.reason);
			switch(status_invalidate) {
				case 'unredeemed':
					dispatch_cv({ type:'SET_UNREDEEM'});
					dispatch_cv({ type:'CHANGE', k:'validated', v:status_invalidate });
					break;

				case 'invalid':
				case 'notvalidated':
					//2
					dispatch_cv({ type:'CHANGE', k:'validated', v:status_invalidate });
					break;
			}

			dispatch_cv({ type:'CHANGE', k:'unredeeming', v:false });
		}, 'JSON');
	};

	if(state_cv.validated == undefined || state_cv.validated === false || state_cv.validated == 'unredeemed') {
		const check_code = isNotEmpty(promotion,'enable_review_coupon',['']) ? true : false;
		const label_action_code = "Send";
		return (
			<Wrapper
				state={state}
				dispatch={dispatch}
				is_builder={props.is_builder}
				has_help={props.has_help}
				promotion={promotion}
				hash={props.hash}
				intl={props.intl}
			>
				{(state_cv.validated == 'unredeemed') && (
					<SuccessMessage
						_class={{ other:'in_top' }}
					>
						<p>{i18n("This prize is available again to redeem")}</p>
					</SuccessMessage>
				)}

				<QrScanInput
					onGetContent={assignCodeFromScan}
					close_on_scan={true}
					scan_error={scan_error}
					change={(k, v) => dispatch_cv({ type:'CHANGE', k, v })} //5380627609
					name="code"
					value={state_cv.code}
					placeholder="Insert the prize reference"
					intl={props.intl}
				/>

				<ButtonCollection
					_class={{ other:'gui_not_in_top flex_button gui_flex_col gui_pos_rel' }}
					style={{ gap:'0.5em' }}
				>
					{(state.param_code) ? (
						<Spinner _class={{ other:'gui_not_in_top' }}>{i18n("Validating")}...</Spinner>
					): (
						<>
							<SaveButton
								onClick={(e,cb) => sendCode(cb, check_code)}
								disabled={!state_cv.code}
							>{label_action_code}</SaveButton>

							<ActionLink
								_fullclass=""
								_href='#validations'
							>Validations</ActionLink>
						</>
					)}

					{(promotion.reward_slider) && (
						<div style={{ marginBlockStart:'3em', textAlign:'center', fontSize:'0.9em', lineHeight:'1.2em' }}>
							<p>{i18n("Users who redeem the prize directly from their mobile will ask you for a security PIN.")}</p>

							<p>{i18n("Security PIN")}: <strong class="pin_preview">{state.point.pin}</strong></p>
						</div>
					)}
				</ButtonCollection>

				{(state_cv.display_pin_layer) && (
					<FloatingLayerPublic
						backButton={() => dispatch_cv({ type:'CHANGE', k:'display_pin_layer', v:false })}
					>
						<div>
							<LeveledHTMLText>
								<h1>{i18n("Security PIN")}</h1>

								<p>{i18n("Give this PIN to users that redeem a prize from their devices")}:</p>
							</LeveledHTMLText>

							<strong class="pin_preview">{state.point.pin}</strong>
						</div>
					</FloatingLayerPublic>
				)}
			</Wrapper>
		)
	}

	const hide_unredeem_codevalidation = promotion?.hide_unredeem_cv ?? false;
	const display_comment_layer = promotion?.allowed_comment_on_redeem ?? false;
	const has_redeemed = (isNotEmpty(state_cv, 'validated') && !['invalid', 'notvalidated', 'unredeemed'].includes(state_cv.validated)) ? true : false;

	const display_comment = (has_redeemed && state_cv.prize && state_cv.validated == 'valid' && display_comment_layer) ? true : false;
	const has_comment = (isNotEmpty(state_cv.CodeValidation, 'validation_comment')) ? state_cv.CodeValidation.validation_comment : false;

	if(state_cv.unredeeming) {
		return (
			<Wrapper
				state={state}
				dispatch={dispatch}
				is_builder={props.is_builder}
				has_help={props.has_help}
				promotion={promotion}
				hash={props.hash}
				intl={props.intl}
			>
				{(state_cv.unredeeming) && (
					<ul class="qr_list gui_pos_rel">
						<li class="gui_not_in_top_huge">
							<Spinner>{i18n("Processing")}...</Spinner>
						</li>
					</ul>
				)}
			</Wrapper>
		)
	}
	if(state_cv.unredeeming) {
		return (
			<Wrapper
				state={state}
				dispatch={dispatch}
				is_builder={props.is_builder}
				has_help={props.has_help}
				promotion={promotion}
				hash={props.hash}
				intl={props.intl}
			>
				{(state_cv.unredeeming) && (
					<ul class="qr_list gui_pos_rel">
						<li class="gui_not_in_top_huge">
							<Spinner>{i18n("Processing")}...</Spinner>
						</li>
					</ul>
				)}
			</Wrapper>
		)
	}

	if(state_cv.validated == 'checking') {
		const participation = state_cv.prize.User;
		const prize = state_cv.prize.PrizeType;
		const stage = state_cv.prize?.Stage ?? {};

		const phone_parts = (isNotEmpty(participation, 'phone', [''])) ? parsePhone(participation.phone) : false;

		return (
			<Wrapper
				state={state}
				dispatch={dispatch}
				is_builder={props.is_builder}
				has_help={props.has_help}
				promotion={promotion}
				hash={props.hash}
				intl={props.intl}
			>
				<div class="pos_header">
					<h1>{i18n("Coupon details")}</h1>
					<p>{i18n("Review the coupon details before marking it as redeemed.")}</p>
				</div>

				<div class="pos_review_info">
					<div class="pos_user">
						<h2 class="pos_subtitle">{i18n("User")}</h2>

						<dl class="answered_data">
							<div>
								<dt class="knk-user">{i18n("***Name")}</dt>
								<dd>{participation?.nickname ?? (participation.first_name && participation.last_name ? participation.first_name + ' ' + participation.last_name : participation.name)}</dd>
							</div>

							{(['4','9'].includes(participation.net_type)) && (//Autologin or CodeLogin
								<div>
									<dt class="knk-user-mention">{i18n("External ID")}</dt>
									<dd>{participation.fbuid}</dd>
								</div>
							)}

							{(isNotEmpty(participation, 'email', [''])) && (
								<div>
									<dt class="knk-email">{i18n("Email")}</dt>
									<dd>{participation.email}</dd>
								</div>
							)}

							{(phone_parts !== false) && (
								<div>
									<dt class="knk-phone">{i18n("Phone")}</dt>
									<dd>{`(+${phone_parts.prefix}) ${phone_parts.number}`}</dd>
								</div>
							)}
						</dl>
					</div>

					<div class="pos_prize">
						<h2 class="pos_subtitle">{i18n("Prize")}</h2>

						<dl class="answered_data">
							<div>
								<dt class="knk-present-fill">{i18n("Prize")}</dt>
								<dd>{`${prize.title} ${(prize.reference != undefined) ? ` (${prize.reference})` : `` }`}</dd>
							</div>

							<div>
								<dt class="knk-redeem-code">{i18n("Code")}</dt>
								<dd>{state_cv.code}</dd>
							</div>

							<div>
								<dt class="knk-calendar">{i18n("Issue date")}</dt>
								<dd>{EPDateFormat(prize.created, 'llll')}</dd>
							</div>

							{(isNotEmpty(prize, 'prize_expiration_date')) && (
								<div>
									<dt class="knk-calendar">{i18n("Expiration date")}</dt>
									<dd>{EPDateFormat(prize.prize_expiration_date, 'llll')}</dd>
								</div>
							)}
						</dl>
					</div>

					{(participation?.quiz_entry?.form != undefined && stage?.form != undefined) && (
						<div class="pos_extra">
							<h2 class="pos_subtitle">{i18n("Extra")}</h2>

							<DisplayDynamicForm 
								form={stage.form}
								values={participation.quiz_entry.form}
								date_format='LL'
								is_builder={true}
								humanize_title={true}
								not_margined={true}
							/>
						</div>
					)}

					<ButtonCollection>
						<SecondaryButton
							onClick={() => dispatch_cv({ type:'RESET' })}
						>Cancel</SecondaryButton>

						<SaveButton
							type="confirm"
							_position="top"
							onClick={(e, cb) => sendCode(cb)}
						>Redeem</SaveButton>
					</ButtonCollection>
				</div>
			</Wrapper>
		)
	}

	return (
		<Wrapper
			state={state}
			dispatch={dispatch}
			is_builder={props.is_builder}
			has_help={props.has_help}
			promotion={promotion}
			hash={props.hash}
			intl={props.intl}
		>	
			<RedeemStatus
				type={state_cv.validated}
				intl={props.intl}
			/>

			{(state_cv.validated == 'used') && (
				<dl class="winner_data">
					<div>
						<dt><strong>{i18n("Reason")}</strong>:</dt>
						<dd><FormattedMessage
							id="This prize was already validated by {validation_point}"
							values={{
								validation_point:<strong>{state_cv.CodeValidation?.validation_site_name ?? state_cv.CodeValidation?.validation_site}</strong>,
							}}
						/></dd>
					</div>

					<div>
						<dt><strong>{i18n("Date")}</strong>:{}</dt>
						<dd>{EPDateFormat(state_cv.CodeValidation.created, format_date)}</dd>
					</div>
				</dl>
			)}

			{(state_cv.prize && state_cv.validated == 'valid') && (
				<dl class="winner_data">
					<div>
						<dt><strong>{i18n("Prize")}</strong>:</dt>

						<dd>{state_cv.prize.PrizeType.title} (<ActionLink
							onClick={(e) => dispatch_cv({ type:'CHANGE', k:'display_prize_layer', v:true })}
						>View</ActionLink>)</dd>
					</div>

					{(state_cv.prize.User.name != '' && state_cv.prize.User.name != 'User not Registered') && (
						<div>
							<dt><strong>{i18n("Winner name")}</strong>:</dt>

							<dd>{state_cv.prize.User.name}</dd>
						</div>
					)}

					{(promotion.pos_message_after_redeem != "") && (
						<div class="support_text gui_not_in_bottom gui_is_not_padded">
							<dt><strong>{i18n("Message")}</strong>:</dt>
							<dd dangerouslySetInnerHTML={{ __html:promotion.pos_message_after_redeem }} />
						</div>
					)}
				</dl>
			)}

			{(state_cv.prize && state_cv.validated == 'valid' && state_cv.prize.User.ticket_url != undefined) && (
				<div class="gui_flexbox gui_flex_col not_in_top" style={{alignItems:'center',textAlign:'center',gap:'0.2em'}}>
					<ActionLink
						onClick={() => dispatch_cv({ type:'CHANGE', k:'show_ticket', v:!state_cv.show_ticket })}
					>{state_cv.show_ticket ? "Hide ticket" : "View ticket"}</ActionLink>

					{(state_cv.show_ticket) && (
						<img src={state_cv.prize.User.ticket_url} />
					)}
				</div>
			)}

			{(promotion.code_validator_support != "") && (
				<div
					class="support_text gui_not_in_bottom gui_is_not_padded"
					dangerouslySetInnerHTML={{ __html:promotion.code_validator_support }}
				/>
			)}

			{(display_comment && has_comment !== false) && (
				<dl class="winner_data">
					<div>
						<dt><strong>{i18n("Note")}</strong><span class="gui_not_in_start_tiny" style={{whiteSpace:'nowrap'}}>(<ActionLink
							onClick={() => dispatch_cv({ type:'CHANGE', k:'display_comment_layer', v:true })}
							autotranslate={false}
						>{i18n("Edit", 'lowercase')}</ActionLink>)</span>:</dt>

						<dd>{has_comment}</dd>
					</div>
				</dl>
			)}

			<div class="additional_actions">
				{(display_comment && has_comment === false) && (
					<ActionLink
						onClick={() => dispatch_cv({ type:'CHANGE', k:'display_comment_layer', v:true })}
						icon='knk-comment'
					>Add a note</ActionLink>
				)}

				{(!hide_unredeem_codevalidation && !state_cv.expired && ['used','valid'].includes(state_cv.validated) ) && (
					<ActionLink
						onClick={() => invalidate()}
						icon='knk-reload'
					>Mark as unredeemed</ActionLink>
				)}
			</div>

			{(['valid'].includes(state_cv.validated)) && (
				<ButtonCollection
					_class={{ other:'gui_not_in_top_big' }}
				>
					<MainButton
						onClick={() => dispatch_cv({ type:'RESET' })}
					>Done</MainButton>
				</ButtonCollection>
			)}

			{(['invalid','used'].includes(state_cv.validated)) && (
				<ButtonCollection
					_class={{ other:'gui_not_in_top_big' }}
				>
					<SecondaryButton
						onClick={() => dispatch_cv({ type:'RESET' })}
					>Back</SecondaryButton>
				</ButtonCollection>
			)}

			{(state_cv.display_prize_layer) && (
				<FloatingLayerPublic
					backButton={() => dispatch_cv({ type:'CHANGE', k:'display_prize_layer', v:false })}
				>
					<WinnerContent
						prize={state_cv.prize.PrizeType}
						user_prize={state_cv.prize.Prize}
						code_validation={state_cv.prize?.CodeValidation ?? null}
						//is_layer={false}
						in_validation={true}
					/>
				</FloatingLayerPublic>
			)}

			{(state_cv.display_comment_layer) && (
				<FloatingLayerPublic
					backButton={() => dispatch_cv({ type:'CHANGE', k:'display_comment_layer', v:false })}
				>
					<Model model={state_cv} change={(k, v) => dispatch_cv({ type:'CHANGE', k, v })}>
						<TextArea
							name="validation_comment"
							_class={{ first:true }}
						>Insert an internal note</TextArea>
					</Model>

					<ButtonCollection
						_class={{ other:'flex_button gui_big_button gui_not_in_top gui_custom_color' }}
					>
						<SaveButton
							onClick={(e, cb) => submitComment((cb))}
						>Save</SaveButton>
					</ButtonCollection>
				</FloatingLayerPublic>
			)}
		</Wrapper>
	)
};

const LoginPage = ({ state, dispatch, ...props }) => {
	const is_builder = props?.is_builder ?? false;

	const point = state?.point ?? null;
	const has_login_image = props?.promotion?.logo_login ?? false;

	const login = (cb) => {
		$.post({
			url:getRemotedbUrl(`/sales/${props.promotion.id}/${props.hash}/login`),
			data:{
				//action:'login',
				_data:encodeBase64Fake({ action:'login', password:point.password }),
			},
			dataType:'JSON',
			success:(response) => {
				if(!response.status) {
					let message_error = response.reason;
					switch (message_error) {
						case 'unknown_password':
						case 'wrong_password':
							message_error = "The password is not valid";
							break;
					}
					dispatch({ type:'SET_ERRORS', errors:{ password:message_error } });
				} else {
					dispatch({ type:'CHANGE', k:'point',v:response.pos });
					window.location.hash = 'selector';
				}

				if(cb != undefined) cb();
			}
		});
	};

	return (
		<Wrapper
			state={state}
			dispatch={dispatch}
			is_builder={props.is_builder}
			is_login={true}
			has_help={props.has_help}
			promotion={props.promotion}
			hash={props.hash}
			enable_logout={true}
			header_props={{
				display:false,
			}}
			intl={props.intl}
		>
			{(has_login_image) && (
				<div class="logo gui_not_in_bottom">
					<img src={has_login_image} height="100%" alt="Logo" />
				</div>
			)}

			{(isNotEmpty(props, 'intro_text')) && (
				<LeveledHTMLText
					_class={{ other:'gui_not_in_bottom_big' }}
				>{props.intro_text}</LeveledHTMLText>
			)}

			<Model
				model={point}
				change={(k, v) => dispatch({ type:'CHANGE_POINT', k, v })}
				errors={state.errors}
			>
				<Input
					_type="password"
					name="password"
					_class={{ first:true }}
				>Enter a password</Input>
			</Model>

			<ButtonCollection
				_class={{ other:'gui_flex_col gui_not_in_top' }}
			>
				<div class="ctas in_top">
					<SaveButton
						_fullclass=""
						onClick={(!is_builder) ? (e,cb) => login(cb) : null}
					>Login</SaveButton>
				</div>

				{(props?.has_help ?? false) && (
					<ActionLink
						_class={{ other:'alone_link' }}
						_href={(!is_builder) ? '#help' : null}
					>Help</ActionLink>
				)}
			</ButtonCollection>
		</Wrapper>
	)
};

const HelpPage = ({ state, dispatch, ...props }) => {
	if(!isNotEmpty(props, 'help_text')) return null;

	return (
		<Wrapper
			state={state}
			dispatch={dispatch}
			is_builder={props.is_builder}
			has_help={props.has_help}
			promotion={props.promotion}
			enable_logout={true}
			hash={props.hash}
			intl={props.intl}
		>
			<LeveledHTMLText>{props.help_text}</LeveledHTMLText>
		</Wrapper>
	)
};

const PointsOfSalePortal = (props) => {
	useCSS('comp/points_of_sale_portal.css');

	const promotion = props?.promotion ?? false;
	const basepath = props?.basepath ?? '';
	const is_builder = props?.is_builder ?? false;
	const pos = props?.pos ?? false;

	const generateURL = (stage_qr, point) => {
		const quantity = state.totals?.[stage_qr.stage_id] ?? 1;
		const code = encodeBase64Fake(`${stage_qr.id}-${stage_qr.code}-${point.id}-${quantity}`)
		return `${props.public_url}promotions/participate/${stage_qr.promotion_id}/${stage_qr.stage_id}?code=${code}`;
	};

	const [ state, dispatch ] = useReducer((state, action) => {
		switch(action.type) {
			case 'SET_ERRORS':
				return {
					...state,
					errors:action.errors,
				};

			case 'CHANGE_POINT':
				let _errors = state.errors;
				if(_errors[action.k] != undefined) delete _errors[action.k];

				return {
					...state,
					point:{
						...state.point,
						[action.k]:action.v,
					},
					errors:_errors,
				};

			case 'CHANGE':
				return {
					...state,
					[action.k]:action.v,
				};

			case 'CHANGE_QTY':
				for(let i in state.stages) {
					if(state.stages[i].id != action.id) continue;
					i = parseInt(i);
					
					state.stages[i][action.k] = action.v;
					//state.stages[i].url = generateURL(state.stages[i], state.point);
					state.totals[state.stages[i]['id']] = action.v;

					//state.changing = i;
					const st = { ...state.stages[i] };

					return {
						...state,
						stages:state.stages.slice(0, i).concat(st).concat(state.stages.slice(i + 1)),
					};
				}
				break;
	
			default:
				return state;
		}
	}, {
		point:pos,
		errors:{},
		totals:{},
		seconds:null,
		stages:[],
		totals:props?.totals ?? {},
		loaded:false,
		param_code:props?.code ?? '',
		has_qr_stages:props?.has_qr_stages ?? false,
	});

	if(promotion === false) return null;

	let MainComponent = LoginPage;
	if(pos !== false){
		MainComponent = !isNotEmpty(state,'param_code',['']) ? ActionSelectorPage : ValidationPortal;
	}

	if(is_builder) {
		switch(props?.actual_section ?? '') {
			case 'selector':
				MainComponent = ActionSelectorPage;
				break;

			case 'help':
				MainComponent = HelpPage;
				break;
		}
	}

	const has_help = isNotEmpty(promotion, 'pos_help_text') ? true : false;

	const shared_props = {
		state,
		dispatch,
		promotion:{
			id:promotion.id,
			logo:promotion?.full_pos_logo_image ?? null,
			logo_login:promotion?.full_code_validator_logo ?? null,
			bottom_image:promotion?.full_code_validator_bottom_image ?? null,
			timezone:promotion?.timezone ?? 'Europe/Madrid',
			code_validator:promotion?.code_validator ?? false,
			code_validator_hash:promotion?.code_validator_hash ?? '',
			code_validator_support:promotion?.code_validator_support ?? '',
			hide_unredeem_cv:promotion?.hide_unredeem_cv ?? false,
			allowed_comment_on_redeem:promotion?.allowed_comment_on_redeem ?? false,
			reward_slider:promotion?.reward_slider ?? false,
			pos_message_after_redeem:promotion?.pos_message_after_redeem ?? false,
			enable_review_coupon:promotion?.enable_review_coupon ?? false,
		},
		is_builder,
		has_help,
		intro_text:promotion.pos_intro_text,
		hash:props.hash,
		intl:props.intl,
		public_url:props.public_url,
		language:props.language,
	};
	
	const layout_props_model = getLayout(promotion);
	const main_color_arr = colorToArray(promotion?.pos_color ?? layout_props_model.color.main_color);
	const main_color = main_color_arr.join(",");

	const over_main_color_color = isColorBright(RGB_2_HEX(`rgba(${main_color}, 1)`)) ? '0, 0, 0' : '255, 255, 255';

	let styles = null;
	if(!is_builder) {
		// In production, we need these variables to be in :root level because we need
		// these variables in FloatingLayers (which are at the body first-child level).
		document.documentElement.style.setProperty('--main-color', main_color);
		document.documentElement.style.setProperty('--btn-text-color', over_main_color_color);
		document.documentElement.style.setProperty('--selection-color', over_main_color_color);
	} else {
		// In builder we need to do this "xapuss" because if we change it to :root level,
		// the builder colors change when accessing the #pos/portal page.
		styles = {
			'--main-color':main_color,
			'--btn-text-color':over_main_color_color,
			'--selection-color':over_main_color_color,
		};
	}

	return (
		<div style={styles}>
			<Router basename={`${basepath}`} hashType="noslash">
				<>
					<Route exact={true} path='/' render={(route) => {
						return (
							<MainComponent
								{...shared_props}
								help_text={promotion.pos_help_text}
							/>
						)
					}} />

					<Route exact={true} path='/selector' render={(route) => {
						if(isNotEmpty(state,'param_code',['']) || !state.has_qr_stages) window.location.hash = `validation_portal`;
						return (
							<ActionSelectorPage
								{...shared_props}
							/>
						)
					}} />

					<Route exact={true} path='/help' render={(route) => {
						if(!has_help) {
							history.back();
							return null;
						}

						return (
							<HelpPage
								{...shared_props}
								help_text={promotion.pos_help_text}
							/>
						)
					}} />

					<Route exact={true} path='/validation_portal' render={(route) => {
						return (
							<ValidationPortal
								{...shared_props}
							/>
						)
					}} />


					<Route exact={true} path='/validations' render={(route) => {
						return (
							<ValidationsPage
								{...shared_props}
							/>
						)
					}} />

					<Route exact={true} path='/list' render={(route) => {
						return (
							<SimpleListPage
								{...shared_props}
								generateURL={generateURL}
							/>
						)
					}} />

					<Route exact={true} path='/list/:id' render={(route) => {
						return (
							<SimpleSingleItemPage
								{...shared_props}
								id={route.match.params.id}
								generateURL={generateURL}
							/>
						)
					}} />

					<Route exact={true} path='/qr_list/:id?' render={(route) => {
						const qr_id = route?.match?.params?.id ?? null;

						window.location.hash = `/list${(qr_id != undefined) ? `/${qr_id}` : ''}`;

						return (
							<QRListPage
								{...shared_props}
								single_id={qr_id}
								generateURL={generateURL}
							/>
						)
					}} />
				</>
			</Router>
		</div>
	)
};

export default injectIntl(PointsOfSalePortal)